import React from 'react';

/**
 * CardGlass: The base glassmorphism container
 */
export const CardGlass = ({ children, title, subtitle, className = '', headerAction, footer }) => (
    <div
        className={`glass-effect p-6 card-hover overflow-hidden relative group ${className}`}
        style={{ borderRadius: 'var(--radius-lg)' }}
    >
        {/* Subtle Glow Overlay */}
        <div className="absolute -top-20 -right-20 w-40 h-40 bg-primary/10 rounded-full blur-3xl opacity-0 group-hover:opacity-100 transition-opacity" />

        {(title || subtitle || headerAction) && (
            <div className="flex items-center justify-between mb-6 relative">
                <div className="flex flex-col gap-1">
                    {title && <h3 className="text-lg font-bold text-white tracking-tight">{title}</h3>}
                    {subtitle && <p className="text-sm" style={{ color: 'var(--text-soft)' }}>{subtitle}</p>}
                </div>
                {headerAction && <div className="flex-shrink-0">{headerAction}</div>}
            </div>
        )}

        <div className="relative z-10">{children}</div>

        {footer && (
            <div
                className="mt-6 pt-4 border-t text-xs flex items-center gap-2 relative z-10"
                style={{ borderColor: 'var(--border-soft)', color: 'var(--text-muted)' }}
            >
                {footer}
            </div>
        )}
    </div>
);

/**
 * StatCard: KPI with icon, value and delta
 */
export const StatCard = ({ label, value, delta, trend = 'up', icon, color = 'var(--primary)', className = '' }) => (
    <CardGlass className={`flex flex-col justify-between min-h-[160px] ${className}`}>
        <div className="flex justify-between items-start">
            <div
                className="w-12 h-12 rounded-xl flex items-center justify-center text-xl transition-transform hover:scale-110"
                style={{ background: `${color}15`, border: `1.5px solid ${color}30`, color }}
            >
                {icon || '📊'}
            </div>
            {delta !== undefined && (
                <span className={`text-xs font-black px-2 py-1 rounded-lg border flex items-center gap-1 ${trend === 'up' ? 'text-success bg-success/10 border-success/20' : 'text-danger bg-danger/10 border-danger/20'
                    }`}>
                    {trend === 'up' ? '↗' : '↘'} {delta}%
                </span>
            )}
        </div>

        <div className="mt-auto">
            <div className="text-3xl font-black text-white tracking-tighter mb-1 transform transition-all group-hover:translate-x-1">
                {value ?? '—'}
            </div>
            <div
                className="text-[10px] uppercase font-black tracking-[0.1em] opacity-80"
                style={{ color: 'var(--text-soft)' }}
            >
                {label}
            </div>
        </div>
    </CardGlass>
);

/**
 * Badge: Status labels with glows
 */
export const Badge = ({ children, status = 'default', className = '' }) => {
    const themes = {
        default: 'bg-white/10 text-slate-300 border-white/10',
        primary: 'bg-primary/10 text-primary border-primary/20 shadow-[0_0_10px_rgba(59,130,246,0.1)]',
        success: 'bg-success/10 text-success border-success/20 shadow-[0_0_10px_rgba(16,185,129,0.1)]',
        warning: 'bg-warning/10 text-warning border-warning/20 shadow-[0_0_10px_rgba(245,158,11,0.1)]',
        danger: 'bg-danger/10 text-danger border-danger/20 shadow-[0_0_10px_rgba(239,68,68,0.1)]',
        info: 'bg-info/10 text-info border-info/20 shadow-[0_0_10px_rgba(59,130,246,0.1)]',
    };

    return (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider border ${themes[status] || themes.default} ${className}`}>
            {children}
        </span>
    );
};

/**
 * SectionHeader: Title, description and actions
 */
export const SectionHeader = ({ title, subtitle, actions, className = '' }) => (
    <div className={`flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10 ${className}`}>
        <div className="flex flex-col gap-2">
            <h1 className="text-4xl font-black text-white tracking-tighter drop-shadow-sm leading-tight">
                {title}<span className="text-glow-primary" style={{ color: 'var(--primary)' }}>.</span>
            </h1>
            {subtitle && <p className="text-lg max-w-2xl font-medium" style={{ color: 'var(--text-soft)' }}>{subtitle}</p>}
        </div>
        {actions && <div className="flex items-center gap-3">{actions}</div>}
    </div>
);

/**
 * Skeleton: Premium Apple-style shimmer
 */
export const Skeleton = ({ width = '100%', height = '20px', className = '' }) => (
    <div
        className={`shimmer rounded-md bg-white/5 ${className}`}
        style={{ width, height }}
    />
);

/**
 * TablePro: Responsive table with premium interactions
 */
export const TablePro = ({ headers, data, renderRow, loading = false, emptyText = "No data found", className = "" }) => (
    <div
        className={`w-full overflow-hidden border glass-effect ${className}`}
        style={{ borderRadius: 'var(--radius-lg)', borderColor: 'var(--border-soft)' }}
    >
        <table className="w-full text-left border-collapse">
            <thead className="bg-[#020617]/40 border-b" style={{ borderColor: 'var(--border-soft)' }}>
                <tr>
                    {headers.map((h, i) => (
                        <th
                            key={i}
                            className="px-6 py-4 text-[10px] font-black uppercase tracking-[0.1em]"
                            style={{ color: 'var(--text-soft)' }}
                        >
                            {h}
                        </th>
                    ))}
                </tr>
            </thead>
            <tbody className="divide-y" style={{ borderColor: 'var(--border-soft)' }}>
                {loading ? (
                    [1, 2, 3, 4, 5].map((i) => (
                        <tr key={i} className="animate-pulse-slow">
                            {headers.map((_, colIdx) => (
                                <td key={colIdx} className="px-6 py-4">
                                    <Skeleton width={colIdx === 0 ? '40px' : '80%'} height="12px" />
                                </td>
                            ))}
                        </tr>
                    ))
                ) : data.length === 0 ? (
                    <tr>
                        <td
                            colSpan={headers.length}
                            className="px-6 py-20 text-center font-medium"
                            style={{ color: 'var(--text-soft)' }}
                        >
                            {emptyText}
                        </td>
                    </tr>
                ) : (
                    data.map((item, idx) => (
                        <tr key={idx} className="hover:bg-white/[0.02] transition-colors group">
                            {renderRow(item, idx)}
                        </tr>
                    ))
                )}
            </tbody>
        </table>
    </div>
);

/**
 * Button: Premium variations
 */
export const Button = ({ children, variant = 'primary', size = 'md', className = '', ...props }) => {
    const variants = {
        primary: 'btn-premium text-white px-6 py-2.5 font-bold shadow-lg shadow-blue-500/20 hover:shadow-blue-500/40 active:scale-95 transition-all text-sm',
        secondary: 'bg-white/5 border border-white/10 text-white hover:bg-white/10 hover:border-white/20 active:scale-95 transition-all px-6 py-2.5 font-bold rounded-xl text-sm',
        ghost: 'hover:text-white hover:bg-white/5 px-4 py-2 rounded-xl transition-all font-bold text-sm',
        danger: 'bg-danger/10 border border-danger/20 text-danger hover:bg-danger/20 active:scale-95 transition-all px-6 py-2.5 font-bold rounded-xl text-sm'
    };

    const style = variant === 'ghost' ? { color: 'var(--text-soft)' } : {};

    return (
        <button className={`${variants[variant] || variants.primary} ${className}`} style={style} {...props}>
            {children}
        </button>
    );
};
/**
 * LoadingSpinner: Premium circular indicator
 */
export const LoadingSpinner = ({ size = 'md', className = '' }) => {
    const sizes = {
        sm: 'w-4 h-4 border-2',
        md: 'w-8 h-8 border-[3px]',
        lg: 'w-12 h-12 border-4'
    };

    return (
        <div className={`flex justify-center items-center py-10 ${className}`}>
            <div className={`animate-spin rounded-full border-white/10 border-t-primary ${sizes[size] || sizes.md}`}></div>
        </div>
    );
};
